import commonParser, {
  type IDiagramParserOutput,
} from '../parsing/CommonParser';
import commonTextGenerator from '../parsing/CommonTextGenerator';
import commonNodeGenerator from '../parsing/CommonNodeGenerator';
import ErrorDiagram from '../common/ErrorDiagram';
import FlowchartDiagram from '../diagrams/flowchart/FlowchartDiagram';
import CycleDiagram from '../diagrams/cycle/CycleDiagram';
import HierarchyDiagram from '../diagrams/hierarchy/HierarchyDiagram';
import type Diagram from './Diagram';
import { type LayoutOptions } from '../types';
import { DiagramDatabase } from '../parsing/DiagramStructure';

// Public API for generating diagrams.
class DiagramGenerator {
  private static readonly parser = commonParser;
  private static readonly textGenerator = commonTextGenerator;

  // Generate a diagram from a string input. The width and height are a hint
  // to the layout engine about the size of the diagram, but the resulting
  // diagram may not be exactly this size.
  //
  // A diagram is always returned, even if there are errors in the generation.
  static generate(
    input: string,
    { widthHint, heightHint }: { widthHint: number; heightHint: number }
  ): Diagram {
    const parseOutput = this.parse(input);
    if (parseOutput.result !== 'success') {
      // Construct a dummy diagram to hold the errors.
      const errorDiagram = new ErrorDiagram({
        width: widthHint,
        height: heightHint,
      });

      parseOutput.errors.forEach(e => errorDiagram.addErrorObject(e));

      return errorDiagram;
    }
    const diagram = this.analyze(parseOutput);

    return diagram;
  }

  // Parse a diagram string into a syntax tree.
  static parse(input: string): IDiagramParserOutput {
    const result = this.parser.parse(input);

    return result;
  }

  // Convert the parsed diagram back to a string representation.
  static toString(parseOutput: IDiagramParserOutput): string {
    /* if (parseOutput.result !== 'success') {
      return '';
    } */
    const output = this.textGenerator.visit(parseOutput.root);
    return output;
  }

  static async toWhiteboard(diagram: Diagram, layoutOptions: LayoutOptions) {
    return diagram.toWhiteboard(layoutOptions);
  }

  static async toSvg(diagram: Diagram, layoutOptions: LayoutOptions) {
    return diagram.toSvg(layoutOptions);
  }

  // Analyze the syntax tree and populate the diagram database.
  static analyze(parseOutput: IDiagramParserOutput): Diagram {
    const database = this.translateSyntaxTree(parseOutput);
    return this.diagramFromDatabase(database);
  }

  static diagramFromDatabase(database: DiagramDatabase): Diagram {
    let diagram: Diagram;

    const diagramClass = DiagramGenerator.diagramClasses().find(
      cls => cls.diagramType === database.diagramType
    );
    if (!diagramClass) {
      // If no specific class is found, default to HierarchyDiagram.
      diagram = new HierarchyDiagram(database);
      diagram.addError(`Unknown diagram type`);
    } else {
      // eslint-disable-next-line new-cap
      diagram = new diagramClass(database);
    }

    return diagram;
  }

  // Translate the syntax tree into tree that can more easily be digested
  // by the diagram's analyze step.
  static translateSyntaxTree(
    parseOutput: IDiagramParserOutput
  ): DiagramDatabase {
    if (parseOutput.result !== 'success') {
      // Return empty database for failed parses
      const database = new DiagramDatabase();
      return database;
    }
    return commonNodeGenerator.visit(parseOutput.root);
  }

  static diagramClasses(): (typeof Diagram)[] {
    return [FlowchartDiagram, CycleDiagram, HierarchyDiagram];
  }
}

export default DiagramGenerator;
